import { useCallback, RefObject } from "react";
import { MouseEvent } from "react";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

interface UseMenuHandlersProps {
  isMobile: boolean;
  isProtectedAdjustment: (transaction: TransactionWithDate | null) => boolean;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  setMenuAnchor: (anchor: HTMLElement | null) => void;
  setSelectedTransaction: (transaction: TransactionWithDate | null) => void;
}

export const useMenuHandlers = ({
  isMobile,
  isProtectedAdjustment,
  tableContainerRef,
  setMenuAnchor,
  setSelectedTransaction,
}: UseMenuHandlersProps) => {
  // Handle menu open
  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>, transaction: TransactionWithDate): void => {
    // Don't open menu for protected adjustments
    if (isProtectedAdjustment(transaction)) {
      return;
    }
    setMenuAnchor(event.currentTarget);
    setSelectedTransaction(transaction);
  }, [isProtectedAdjustment, setMenuAnchor, setSelectedTransaction]);

  const handleMenuClose = useCallback((): void => {
    setMenuAnchor(null);
    setSelectedTransaction(null);
  }, [setMenuAnchor, setSelectedTransaction]);

  // Handle wheel events for proper scrolling across entire container (mobile view)
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!isMobile) return; // Only handle for mobile view
    
    const container = tableContainerRef.current;
    if (!container) return;
    
    // Only handle if the container can scroll
    const canScroll = container.scrollHeight > container.clientHeight;
    if (!canScroll) return;
    
    // Check if we're at boundaries
    const isAtTop = container.scrollTop <= 0;
    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;
    
    // If at boundaries, don't prevent default to allow page scroll
    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      return;
    }
    
    // Scroll the container and prevent default to stop page scroll
    container.scrollTop += e.deltaY;
    e.preventDefault();
    e.stopPropagation();
  }, [isMobile, tableContainerRef]);

  return {
    handleMenuOpen,
    handleMenuClose,
    handleWheel,
  };
};
